import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Link,
  Divider
} from '@mui/material';
// Import our custom Grid component instead
import Grid from '../components/Grid';

import LaunchIcon from '@mui/icons-material/Launch'; // Icon for external links

const goldSpecificSources = [
  { name: 'World Gold Council', url: 'https://www.gold.org/', description: 'Market development organization for the gold industry' },
  { name: 'Kitco', url: 'https://www.kitco.com/', description: 'Leading source of gold news and gold market data' },
  { name: 'Gold Price', url: 'https://goldprice.org/', description: 'Live gold price charts and data' },
  { name: 'BullionVault', url: 'https://www.bullionvault.com/', description: 'Physical gold and silver market insights' }
];

const financialSources = [
  { name: 'Financial Times', url: 'https://www.ft.com/', description: 'Global financial news and analysis' },
  { name: 'Wall Street Journal', url: 'https://www.wsj.com/', description: 'Business and financial market news' },
  { name: 'CNBC', url: 'https://www.cnbc.com/', description: 'Real-time financial market coverage' },
  { name: 'Bloomberg', url: 'https://www.bloomberg.com/', description: 'Business and markets news and data' }
];

const socialSources = [
  { name: 'Twitter/X', url: 'https://twitter.com', description: 'Real-time insights from gold market experts' },
  { name: 'TradingView', url: 'https://www.tradingview.com/', description: 'Technical analysis and trading ideas' },
  { name: 'Investing.com', url: 'https://www.investing.com/', description: 'Financial markets platform and tools' }
];

const renderSourceList = (title: string, description: string, sources: typeof goldSpecificSources) => (
  <Grid item xs={12} md={6} lg={4}>
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" component="h2" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {description}
        </Typography>
        <List dense disablePadding>
          {sources.map((source, index) => (
            <React.Fragment key={source.name}>
              <ListItem disableGutters>
                <ListItemText
                  primary={
                    <Link
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      {source.name}
                      <LaunchIcon sx={{ fontSize: '0.9rem', ml: 0.5 }} />
                    </Link>
                  }
                  secondary={source.description}
                />
              </ListItem>
              {index < sources.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
      </CardContent>
    </Card>
  </Grid>
);

const SourcesPage: React.FC = () => (
  <Box>
    {/* Page Header */}
    <Box sx={{ mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom color="secondary.main">
        Gold Market Data Sources
      </Typography>
      <Typography variant="body1" color="text.secondary">
        XAUJournal collects insights and data from a variety of reputable sources to provide you with comprehensive gold market analysis.
      </Typography>
    </Box>

    {/* Source Lists Grid */}
    <Grid container spacing={3} sx={{ mb: 4 }}>
      {renderSourceList(
        'Gold-Specific Resources',
        'Specialized platforms focused on gold markets and precious metals:',
        goldSpecificSources
      )}
      {renderSourceList(
        'Financial News & Analysis',
        'Trusted financial publications with coverage of precious metals markets:',
        financialSources
      )}
      {renderSourceList(
        'Social Media & Expert Platforms',
        'Platforms where gold market experts share insights and analysis:',
        socialSources
      )}
    </Grid>

    {/* Disclosure Card */}
    <Card sx={{ backgroundColor: 'background.paper' }}>
      <CardContent>
        <Typography variant="h6" component="h2" gutterBottom>
          Data Usage Disclosure
        </Typography>
        <Typography variant="body2" color="text.secondary">
          All market data and expert insights are collected from public sources and used for informational purposes only. XAUJournal is not affiliated with any of the sources listed above and does not claim ownership of their content. Please refer to each source's terms of service for more information.
        </Typography>
      </CardContent>
    </Card>
  </Box>
);

export default SourcesPage;